import {StoppageGroup, RcfaStoppageKind as StoppageKind} from '@domain/shared'
import {StoppageCodeLabel, StoppageInfo} from '@domain/stoppages'
import {DataTable, DataTableRef} from '@hconnect/common/maintain-datatable'
import {
  FilterWrapper,
  useListFilters,
  useListQuerySync,
  FilterActionOperation,
  FilterActionType
} from '@hconnect/common/maintain-filters'
import {downloadBlob} from '@hconnect/common/utils'
import {PageContainer, PageTitle} from '@hconnect/uikit/src/lib2'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import moment from 'moment'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useSearchParams} from 'react-router'

import {useCheckPlantPermission} from '../../hooks'
import {FEATURE_NAMESPACE} from '../../localization'
import {useStoppageNavigation} from '../../providers'
import {formatDate} from '../../utils'
import {RequestAccessDialog} from '../request-access-dialog'
import {StoppageLinkModal} from '../stoppage-link-modal'
import {StoppageDetail} from '../StoppageDetail'
import {StoppageRcfaButton} from '../StoppageRcfaButton'
import {StoppageTitleButton} from '../StoppageTitleButton'

import {useGetStoppages} from './hooks/useGetStoppages'
import {useStoppageListExport} from './hooks/useStoppageListExport'
import {getAvailableStoppageFilters} from './stoppageList.filter'
import {getAvailableColumns} from './stoppageList.orderBy'
import {StoppageListProperty} from './stoppageList.types'

const getStoppageDuration = (duration?: number) => {
  return duration ? moment.duration(duration, 'seconds').humanize() : '-'
}
const getPlannedStoppageDuration = (groupStoppages?: StoppageGroup[]) =>
  groupStoppages
    ?.filter(({stoppageKind}) => stoppageKind === StoppageKind.PlannedStoppage)
    .reduce((acc, stoppage) => acc + (stoppage.duration || 0), 0) || 0

export const StoppageList = () => {
  const theme = useTheme()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const tableRef = useRef<DataTableRef | null>(null)
  const [activeStoppage, setActiveStoppage] = useState<StoppageInfo | undefined>()
  const [requestAccessStoppage, setRequestAccessStoppage] = useState<StoppageInfo | undefined>()
  const [searchParams] = useSearchParams()
  const pageSize = parseInt(searchParams.get('rows') || '10')
  const pageNumber = parseInt(searchParams.get('page') || '0')
  const {navigateToAnalysis, navigateToCreateAnalysis} = useStoppageNavigation()

  const [selectedStoppage, setSelectedStoppage] = React.useState<StoppageInfo | null>(null)
  const checkPlantPermissions = useCheckPlantPermission()

  const availableColumns = getAvailableColumns()
  const availableFilters = getAvailableStoppageFilters()
  const {filterState, apiFilters, dispatch} = useListFilters(
    availableFilters,
    searchParams.get('filters')
  )

  const {handlePageChange, handleRowsPerPageChange} = useListQuerySync({
    filters: apiFilters,
    filtersEnabled: true,
    orderEnabled: false
  })

  const {data, isPending: isLoading} = useGetStoppages({
    pageSize,
    pageNumber,
    filters: apiFilters,
    orderBy: [{property: 'start', descending: true}]
  })

  const {mutateAsync: downloadAsync, isPending: isDownloading} = useStoppageListExport()

  const mandatoryFilter = apiFilters.some((filter) => filter.property === 'rcfaDuration')

  const canCreateAnalysis = (plantId?: string) => {
    return plantId ? checkPlantPermissions(['CREATE_ROOT_CAUSE_FAILURE_ANALYSES'], plantId) : false
  }

  const downloadExcel = async () => {
    const blob = await downloadAsync(apiFilters)
    downloadBlob(blob, `stoppage-list_${new Date().toISOString()}.xlsx`)
  }

  const handleStoppageButtonClick = async ({
    event,
    stoppage
  }: {
    event?: React.MouseEvent<HTMLLIElement | HTMLButtonElement>
    stoppage: StoppageInfo
  }) => {
    event?.stopPropagation()

    const analysisExists = Boolean(stoppage?.analysisId)
    const analysisIsPrivate = Boolean(stoppage?.isPrivateForUser)
    const hasCreatePermission = canCreateAnalysis(stoppage.plantId)

    if (!stoppage.plantId) return

    if (analysisIsPrivate) {
      setRequestAccessStoppage(stoppage)
    } else if (analysisExists) {
      await navigateToAnalysis(stoppage)
    } else if (!analysisExists && hasCreatePermission) {
      await navigateToCreateAnalysis(stoppage)
    }
  }

  const customColumnRender = (columnName: string, data: StoppageInfo) => {
    const plannedStoppagesDuration = getPlannedStoppageDuration(data?.groupStoppages)

    switch (columnName) {
      case 'Title':
        return (
          <StoppageTitleButton
            stoppage={data}
            canCreateAnalysis={canCreateAnalysis(data?.plantId)}
            onClick={handleStoppageButtonClick}
          />
        )
      case 'StoppageCode':
        return (
          <StoppageCodeLabel
            stoppageCode={data?.stoppageCode}
            groupStoppages={data?.groupStoppages}
          />
        )
      case 'stoppageStart':
        return (
          <Typography variant="body1">{formatDate(data?.groupStart || data?.start)}</Typography>
        )
      case 'stoppageEnd':
        return <Typography variant="body1">{formatDate(data?.groupEnd || data?.end)}</Typography>
      case 'duration':
        return (
          <Typography variant="body1" noWrap display="inline-flex" alignItems="center" gap={1}>
            {getStoppageDuration(data?.groupDuration)}
            {!!plannedStoppagesDuration && data.groupDuration && (
              <Tooltip
                title={
                  <>
                    <Typography variant="body2">
                      {getStoppageDuration(data.groupDuration - plannedStoppagesDuration)}{' '}
                      {t('stoppageType.unplanned')}
                      <br />
                      {getStoppageDuration(plannedStoppagesDuration)} {t('stoppageType.planned')}
                    </Typography>
                  </>
                }
              >
                <InfoOutlinedIcon color="info" data-test-id="stoppage-list-duration-info" />
              </Tooltip>
            )}
          </Typography>
        )
      case 'plant':
        return (
          <Typography variant="body1">{`${data?.plantName} (${data?.countryCode})`}</Typography>
        )
      case 'actions':
        return (
          <StoppageRcfaButton
            stoppage={data}
            setActiveStoppage={setActiveStoppage}
            canCreateAnalysis={canCreateAnalysis(data?.plantId)}
            onClick={handleStoppageButtonClick}
            ButtonProps={{id: `stoppage-button-${data.id}`}}
          />
        )
      default:
        return data[columnName] ?? ''
    }
  }

  const headerButton = (
    <Box sx={{float: 'left'}}>
      <ToggleButtonGroup
        exclusive
        value={mandatoryFilter ? 'mandatory' : 'all'}
        onChange={(_, value: string) => {
          if (value === 'mandatory') {
            dispatch({
              type: FilterActionType.ADD_TEXT,
              payload: {
                filterId: 'rcfaDuration',
                value: '86400',
                operation: FilterActionOperation.GREATER_OR_EQUAL
              }
            })
          } else {
            dispatch({
              type: FilterActionType.REMOVE,
              payload: {filterId: 'rcfaDuration', value: ''}
            })
          }
        }}
        sx={{
          display: 'flex',
          border: '1px solid #f5f6f7',
          height: '48px',
          backgroundColor: '#f5f6f7'
        }}
      >
        <StyledToggleButton value={'all'} aria-label={t('label.all')} data-test-id={'display-all'}>
          <Typography variant="h5">{t('label.all')}</Typography>
        </StyledToggleButton>
        <StyledToggleButton
          value={'mandatory'}
          aria-label={t('label.mandatory')}
          data-test-id={'display-mandatory'}
        >
          <Typography variant="h5">{t('label.mandatory')}</Typography>
        </StyledToggleButton>
      </ToggleButtonGroup>
    </Box>
  )

  // set default filter to YTD if no filters are applied
  useEffect(() => {
    if (apiFilters.length) return
    dispatch({
      type: FilterActionType.ADD_DATE_RANGE,
      payload: {
        filterId: 'start',
        value: {
          startDate: moment().startOf('year').toDate(),
          endDate: moment().toDate()
        }
      }
    })
    dispatch({
      type: FilterActionType.SET_FILTER_VISIBLE,
      payload: {
        filterId: 'start',
        value: undefined
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PageContainer>
      <Box display="flex" mb={3} flexWrap="wrap" gap={2} justifyContent="space-between">
        <PageTitle mb={0} data-test-id="stoppage-list-title" className="text-foreground_inverted">
          {t('label.stoppageList')}
        </PageTitle>
        <FilterWrapper
          filters={filterState}
          dispatch={dispatch}
          translationNamespace={FEATURE_NAMESPACE}
        />
      </Box>
      <DataTable<StoppageListProperty, StoppageInfo>
        ref={tableRef}
        translationNamespace={FEATURE_NAMESPACE}
        tableHeaderSettings={{
          showDescription: true,
          customButton: headerButton,
          download: {
            showDownload: true,
            downloadFunction: downloadExcel,
            isDownloading
          }
        }}
        customColumnRender={customColumnRender}
        tableId={'stoppages-list'}
        availableColumns={availableColumns}
        hoverColor={theme.palette.grey.A100}
        data={data}
        isLoadingData={isLoading}
        setActiveItem={setSelectedStoppage}
        detailComponent={
          selectedStoppage && (
            <StoppageDetail
              stoppage={selectedStoppage}
              onLink={
                selectedStoppage?.analysisId ? undefined : () => setActiveStoppage(selectedStoppage)
              }
              closeAction={() => {
                tableRef.current?.clearDetail()
                setSelectedStoppage(null)
              }}
              moveToNextStep={(e) =>
                handleStoppageButtonClick({event: e, stoppage: selectedStoppage})
              }
              showNextStep={
                !selectedStoppage?.analysisId && canCreateAnalysis(selectedStoppage.plantId)
              }
            />
          )
        }
        paginationSettings={{
          page: pageNumber,
          rowsNumber: pageSize,
          handlePageChange,
          handleRowsPerPageChange
        }}
      />
      {activeStoppage !== undefined && (
        <StoppageLinkModal
          handleClose={() => setActiveStoppage(undefined)}
          onSuccess={() => {
            tableRef.current?.clearDetail()
            setSelectedStoppage(null)
          }}
          stoppage={activeStoppage}
        />
      )}

      <RequestAccessDialog
        open={requestAccessStoppage !== undefined}
        onClose={() => setRequestAccessStoppage(undefined)}
        analysisId={requestAccessStoppage?.analysisId}
        analysisOwner={requestAccessStoppage?.analysisOwner}
      />
    </PageContainer>
  )
}

const StyledToggleButton = styled(ToggleButton)({
  width: 'fit-content',
  borderRadius: '6px !important',
  border: 'transparent !important',
  display: 'flex',
  gap: 6,
  '&.Mui-selected': {
    backgroundColor: '#016AD4 !important',
    color: '#FFFF !important'
  },
  '&.MuiToggleButtonGroup-grouped:not(.Mui-selected):hover': {
    background: 'transparent !important'
  },
  background: 'transparent !important'
})
